import { useEffect, useMemo, useState } from "react";
import { apiGet } from "../api/client";
import {
  AnalyticsSidebar,
  AnalyticsStats,
  BarGraph,
  type AnalyticsMode,
} from "../components/AnalyticsChart";

const MODES: AnalyticsMode[] = [
  {
    name: "appts_scheduled",
    header: "Appointments scheduled",
    type: "cat",
    text: "Appointments",
    parent: true,
    prefix: "appts_",
  },
  {
    name: "appts_scheduled",
    header: "Appointments scheduled",
    type: "subcat",
    text: "Booked",
  },
  {
    name: "appts_rescheduled",
    header: "Appointments rescheduled",
    type: "subcat",
    text: "Rescheduled",
    negative: true,
  },
  {
    name: "appts_noshow",
    header: "Patients did not show up",
    type: "subcat",
    text: "No-shows",
    negative: true,
  },
  {
    name: "appts_cancelled",
    header: "Appointments cancelled",
    type: "subcat",
    text: "Cancelled",
    negative: true,
  },
  {
    name: "appts_checkin",
    header: "Patients checked in via NexHealth",
    type: "subcat",
    text: "Checkins via NexHealth",
  },
  {
    name: "reminders",
    header: "Appointment reminders sent",
    type: "cat",
    text: "Reminders sent",
  },
  {
    name: "copays_processed",
    header: "Co-payments processed",
    type: "cat",
    text: "Co-pays processed",
    currency: true,
  },
  {
    name: "patient_satisfaction",
    header: "Average patient satisfaction score",
    type: "cat",
    text: "Patient satisfaction",
    fraction: "5",
  },
];

export function AnalyticsPage() {
  const [mode, setMode] = useState("appts_scheduled");
  const [unit, setUnit] = useState<"months" | "weeks" | "days">("months");
  const [sidebar, setSidebar] = useState<Array<[string, number]>>([]);
  const [amounts, setAmounts] = useState<Array<[number, number]>>([]);

  useEffect(() => {
    apiGet<
      Array<{
        sidebar: Array<[string, number]>;
        amounts: Array<[number, number]>;
      }>
    >("/analytics", { mode, unit, get_sidebar: true }).then((d) => {
      setSidebar(d?.[0]?.sidebar ?? []);
      setAmounts(d?.[0]?.amounts ?? []);
    });
  }, [mode, unit]);

  const modes = useMemo(() => {
    const map = new Map(sidebar.map(([k, v]) => [k, v]));
    return MODES.map((m) => {
      if (m.parent) return m;
      const num = map.get(m.name);
      if (num == null) return m;
      const sb_color =
        num > 0
          ? m.negative
            ? "red"
            : "green"
          : num === 0
            ? "gray"
            : m.negative
              ? "green"
              : "red";
      return { ...m, num, sb_color };
    });
  }, [sidebar]);

  const max = useMemo(() => {
    const mdesc = MODES.find((m) => m.name === mode && !m.parent);
    if (mdesc?.fraction) return Number(mdesc.fraction);
    return Math.max(...amounts.map((a) => a[1]), 1);
  }, [amounts, mode]);

  const mdesc = MODES.find((m) => m.name === mode && !m.parent) || MODES[1];
  const latest = amounts[amounts.length - 1]?.[1];
  const prev = amounts[amounts.length - 2]?.[1];
  const diff =
    latest != null && prev != null ? Math.round(latest - prev) : null;

  const description =
    diff == null
      ? undefined
      : diff > 0
        ? `${diff} more than the ${unit.slice(0, -1)} before`
        : diff < 0
          ? `${Math.abs(diff)} less than the ${unit.slice(0, -1)} before`
          : `Same as the ${unit.slice(0, -1)} before`;

  const color =
    diff == null
      ? undefined
      : diff > 0
        ? mdesc.negative
          ? ("red" as const)
          : ("green" as const)
        : diff < 0
          ? mdesc.negative
            ? ("green" as const)
            : ("red" as const)
          : ("gray" as const);

  const headline =
    diff == null
      ? "Hard to say about"
      : (diff >= 0 && !mdesc.negative) || (diff <= 0 && mdesc.negative)
        ? "We did well"
        : "Could have been better";

  return (
    <div className="ne__analytics">
      <header className="ne__analytics__header">
        <h2>
          <span className="ne__analytics__headline">{headline}</span>{" "}
          <span className="ne__analytics__period">
            <select
              value={unit}
              onChange={(e) => setUnit(e.target.value as typeof unit)}
              aria-label="Analytics period"
            >
              <option value="months">last month</option>
              <option value="weeks">last week</option>
              <option value="days">yesterday</option>
            </select>
          </span>
        </h2>
      </header>
      <div className="ne__analytics__view">
        <AnalyticsSidebar modes={modes} activeMode={mode} onSelect={setMode} />
        <BarGraph
          amounts={amounts}
          max={max}
          currency={mdesc.currency}
          fraction={mdesc.fraction}
        />
        <AnalyticsStats
          value={latest}
          header={mdesc.header}
          description={description}
          color={color}
          currency={mdesc.currency}
          fraction={mdesc.fraction}
        />
      </div>
    </div>
  );
}
